/**
 * Option builders for the session appointment form. Pure so the provider
 * calendar and the in-session scheduler derive identical dropdowns from
 * their own data sources.
 *
 * Each builder keeps the currently-selected value renderable — care teams,
 * caseloads, and session-type policy all load asynchronously and can legitimately
 * exclude a value stored on an existing appointment.
 */

import type { RelatedPerson } from '@medplum/fhirtypes';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';
import { formatFhirHumanName } from '@/pages/appointments/utils/fhirHelpers';
import type { LiveCalendarEpisodeDetail } from './useCalendarAppointmentFormOptions';

export interface SelectOption {
  value: string;
  label: string;
}

/** Episode ("Case") options, keeping an off-caseload selection renderable. */
export function buildEpisodeOptions(args: {
  episodeSelectData: SelectOption[];
  episodeDetails: LiveCalendarEpisodeDetail[];
  selectedEpisodeId: string | null;
  /** Client name from the appointment being edited. */
  fallbackLabel?: string;
}): SelectOption[] {
  const { episodeSelectData, episodeDetails, selectedEpisodeId, fallbackLabel } = args;
  if (!selectedEpisodeId || episodeSelectData.some((o) => o.value === selectedEpisodeId)) {
    return episodeSelectData;
  }
  const detail = episodeDetails.find((e) => e.id === selectedEpisodeId);
  if (detail) {
    return [
      ...episodeSelectData,
      { value: detail.id, label: `${detail.clientName} - ${detail.episodeDisplayName}` },
    ];
  }
  /** Legacy or transferred episode outside the viewer's care-managed list. */
  return [...episodeSelectData, { value: selectedEpisodeId, label: fallbackLabel ?? 'Client' }];
}

/**
 * Provider options the viewer may schedule as. Scheduling on another
 * practitioner's calendar is deferred, so the calendar hook only marks the
 * viewer as `canManage`. Not limited to the selected episode's care team:
 * pick the provider first, then that provider's clients.
 */
export function buildSessionProviderOptions(args: {
  canManageByOwnerId: ReadonlyMap<string, boolean>;
  selectedProviderId: string;
  practitionerDisplayById: ReadonlyMap<string, string>;
  fallbackLabel?: string;
}): SelectOption[] {
  const {
    canManageByOwnerId,
    selectedProviderId,
    practitionerDisplayById,
    fallbackLabel,
  } = args;
  const options = [...canManageByOwnerId.entries()]
    .filter(([, canManage]) => canManage)
    .map(([id]) => ({
      value: id,
      label: practitionerDisplayById.get(id) ?? id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (selectedProviderId && !options.some((o) => o.value === selectedProviderId)) {
    return [
      ...options,
      {
        value: selectedProviderId,
        label:
          practitionerDisplayById.get(selectedProviderId) ?? fallbackLabel ?? selectedProviderId,
      },
    ];
  }
  return options;
}

function sessionTypeLabel(type: AppointmentSessionType): string {
  return type.description;
}

/**
 * Session types approved for the episode, keeping a stored out-of-policy type
 * renderable when editing instead of blanking the field — including when the
 * viewer has no catalog access to that HealthcareService.
 */
export function buildSessionTypeOptions(args: {
  episodeFilteredTypes: AppointmentSessionType[];
  allProviderTypes: AppointmentSessionType[];
  selectedSessionTypeId: string;
  editing?: boolean;
  /** Appointment `serviceType` display when the catalog row is not loaded. */
  fallbackLabel?: string;
}): SelectOption[] {
  const {
    episodeFilteredTypes,
    allProviderTypes,
    selectedSessionTypeId,
    editing,
    fallbackLabel,
  } = args;
  const data = episodeFilteredTypes.map((t) => ({ value: t.id, label: sessionTypeLabel(t) }));
  if (editing && selectedSessionTypeId && !data.some((o) => o.value === selectedSessionTypeId)) {
    const stored = allProviderTypes.find((t) => t.id === selectedSessionTypeId);
    data.push({
      value: selectedSessionTypeId,
      label: stored ? sessionTypeLabel(stored) : (fallbackLabel?.trim() || selectedSessionTypeId),
    });
  }
  return data;
}

/** Stand-in for a participant whose resource has not resolved to a name. */
export function participantFallbackLabel(id: string): string {
  return `Participant ${id.slice(0, 8)}…`;
}

/**
 * Attendee options as full refs: the episode's client plus its `participants`
 * extension members (RelatedPerson).
 */
export function buildSessionAttendeeOptions(args: {
  episode?: LiveCalendarEpisodeDetail | null;
  /** Edit-mode fallback when the episode isn't in the viewer's care-managed list. */
  fallback?: {
    patientId?: string;
    clientDisplay?: string;
    relatedPersons?: { id: string; display?: string }[];
  };
  selectedRefs: string[];
}): SelectOption[] {
  const { episode, fallback, selectedRefs } = args;
  const options: SelectOption[] = [];

  const participantLabel = (id: string, display?: string): string =>
    display ?? participantFallbackLabel(id);

  if (episode) {
    options.push({ value: `Patient/${episode.patientId}`, label: episode.clientName });
    for (const p of episode.participantRefs) {
      options.push({ value: `RelatedPerson/${p.id}`, label: participantLabel(p.id, p.display) });
    }
  } else if (fallback) {
    if (fallback.patientId) {
      options.push({
        value: `Patient/${fallback.patientId}`,
        label: fallback.clientDisplay ?? 'Client',
      });
    }
    for (const p of fallback.relatedPersons ?? []) {
      options.push({ value: `RelatedPerson/${p.id}`, label: participantLabel(p.id, p.display) });
    }
  }

  for (const ref of selectedRefs) {
    if (!options.some((o) => o.value === ref)) {
      options.push({ value: ref, label: ref });
    }
  }
  return options;
}

/** Ref → label lookup for persisting attendee display names. */
export function attendeeLabelMap(options: SelectOption[]): Map<string, string> {
  return new Map(options.map((o) => [o.value, o.label]));
}

/**
 * Widens the attendee pool with the client's other `RelatedPerson` contacts.
 * Picking an episode pre-selects every attendee it knows about, so without these
 * the participants table has nobody left to offer.
 */
export function withRelatedPersonAttendeeOptions(
  options: SelectOption[],
  relatedPersons: RelatedPerson[],
): SelectOption[] {
  const merged = [...options];
  for (const relatedPerson of relatedPersons) {
    if (!relatedPerson.id || relatedPerson.active === false) continue;
    const value = `RelatedPerson/${relatedPerson.id}`;
    const label =
      formatFhirHumanName(relatedPerson.name?.[0])
      ?? participantFallbackLabel(relatedPerson.id);
    const existing = merged.findIndex((option) => option.value === value);
    if (existing === -1) {
      merged.push({ value, label });
      continue;
    }
    /** Selected refs the episode did not recognize are stored with the ref itself
     * as their label; replace that placeholder once the contact resolves. */
    if (merged[existing].label === value) {
      merged[existing] = { value, label };
    }
  }
  return merged;
}

/** Session type id → duration, so picking a type can advance the end time. */
export function sessionTypeDurationMap(types: AppointmentSessionType[]): Map<string, number> {
  return new Map(types.map((t) => [t.id, t.durationMinutes]));
}

/**
 * End-time label `durationMinutes` after `startTimeLabel`, snapped to 15 minutes.
 * Returns undefined when the start label is unparseable.
 */
export function endTimeForDuration(
  startTimeLabel: string,
  durationMinutes: number,
): string | undefined {
  const match = startTimeLabel.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return undefined;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  const snapped = Math.round((hour * 60 + minute + durationMinutes) / 15) * 15;
  const endHour = Math.floor(snapped / 60) % 24;
  const endMinute = snapped % 60;
  const hour12 = endHour % 12 === 0 ? 12 : endHour % 12;
  return `${hour12}:${String(endMinute).padStart(2, '0')} ${endHour >= 12 ? 'PM' : 'AM'}`;
}

/**
 * Default session type for a case: the episode type's standard type when the
 * provider can deliver it, else the first schedulable option. Returns undefined
 * when the current selection is already valid or nothing is schedulable.
 */
export function resolveDefaultSessionTypeId(args: {
  schedulableTypes: AppointmentSessionType[];
  currentSessionTypeId: string;
  standardSessionTypeId?: string;
}): string | undefined {
  const { schedulableTypes, currentSessionTypeId, standardSessionTypeId } = args;
  if (schedulableTypes.length === 0) return undefined;
  if (currentSessionTypeId && schedulableTypes.some((t) => t.id === currentSessionTypeId)) {
    return undefined;
  }
  return standardSessionTypeId && schedulableTypes.some((t) => t.id === standardSessionTypeId)
    ? standardSessionTypeId
    : schedulableTypes[0].id;
}

/**
 * Site whose rooms and equipment are bookable for the current modality.
 * Empty when the session reserves no physical space.
 */
export function resolveSchedulingSiteId(values: {
  modality: 'in-person' | 'telehealth' | 'hybrid';
  siteId: string;
  telehealthFromOffice: boolean;
  officeForTelehealth: string;
}): string {
  if (values.modality === 'telehealth') {
    return values.telehealthFromOffice ? values.officeForTelehealth : '';
  }
  return values.siteId !== 'telehealth' ? values.siteId : '';
}
